import { Box, Drawer, DrawerProps, useTheme } from '@mui/material';
import { jobDrawerWidth } from 'components/atoms/FlexContainers/FlexContainers';
import { SITE_HEADER_HEIGHT } from 'components/SiteHeader/SiteHeader';
import { Job } from 'interfaces/job';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef } from 'react';
import { useJob } from 'utils/queryHooks/jobs/useJob';
import { JobDetails } from './JobDetails';

interface SharedJobDetailsProps {
  currentJobId: string;
  prefetchedJobInfo?: Partial<Job>;
  skipQuery?: boolean;
  onClose?: () => void;
  onJobLoaded?: (job: Job) => void;
}

const useJobDrawerData = ({
  currentJobId,
  prefetchedJobInfo,
  jobDrawerOpen,
  skipQuery,
}: SharedJobDetailsProps & { jobDrawerOpen: boolean }) => {
  return useJob<Job>(currentJobId, {
    refetchOnMount: 'always',
    enabled: !isEmpty(currentJobId) && jobDrawerOpen && !skipQuery,
    placeholderData: prefetchedJobInfo as Job,
  });
};

export const EmbeddedJobDetailsPanel: React.FC<SharedJobDetailsProps> = ({
  currentJobId,
  prefetchedJobInfo,
  skipQuery,
  onClose,
  onJobLoaded,
}) => {
  const theme = useTheme();
  const panelRef = useRef<HTMLDivElement>(null);
  const { data: job, isLoading } = useJobDrawerData({
    currentJobId,
    prefetchedJobInfo,
    skipQuery,
    jobDrawerOpen: !isEmpty(currentJobId),
    onClose,
  });

  useEffect(() => {
    if (job?.id) {
      onJobLoaded?.(job);
    }
  }, [job, onJobLoaded]);

  useEffect(() => {
    if (panelRef.current) {
      panelRef.current.scrollTop = 0;
    }
  }, [currentJobId]);

  return (
    <Box
      ref={panelRef}
      aria-label="Job details"
      sx={{
        width: jobDrawerWidth,
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <JobDetails
        job={job}
        hasPrefetchedData={Boolean(prefetchedJobInfo)}
        isLoading={isLoading}
        onClosed={
          onClose
            ? (event) => {
                event.stopPropagation();
                onClose();
              }
            : undefined
        }
      />
    </Box>
  );
};

export const JobDrawer: React.FC<
  React.PropsWithChildren<{
    headerHeight?: number | string;
    jobDrawerOpen: boolean;
    setJobDrawerOpen: (open: boolean) => void;
    currentJobId: string;
    prefetchedJobInfo?: Partial<Job>;
    skipQuery?: boolean;
    anchor?: 'right' | 'left' | 'top' | 'bottom';
    sx?: DrawerProps['sx'];
  }>
> = ({
  prefetchedJobInfo,
  jobDrawerOpen,
  setJobDrawerOpen,
  currentJobId,
  headerHeight = SITE_HEADER_HEIGHT,
  skipQuery,
  anchor = 'right',
  sx,
}) => {
  const theme = useTheme();

  const { data: job, isLoading } = useJobDrawerData({
    currentJobId,
    prefetchedJobInfo,
    skipQuery,
    jobDrawerOpen,
  });

  return (
    <Drawer
      key={job?.id}
      anchor={anchor}
      open={jobDrawerOpen}
      onClick={(event) => event.stopPropagation()}
      onClose={() => setJobDrawerOpen(false)}
      variant={'persistent'}
      ModalProps={{ keepMounted: false }}
      sx={{
        width: jobDrawerWidth,
        ...sx,
        [`& .MuiDrawer-paper`]: {
          width: jobDrawerWidth,
          zIndex: theme.zIndex.appBar - 1,
          height: `calc(100% - ${headerHeight})`,
          marginTop: headerHeight,
          ...((sx as any)?.['& .MuiDrawer-paper'] || {}),
        },
      }}
    >
      {/* the keepMounted = false is having issues in react 18 therefore we use this boolean check to prevent rendering unneeded components */}
      {jobDrawerOpen && (
        <JobDetails
          job={job}
          hasPrefetchedData={Boolean(prefetchedJobInfo)}
          isLoading={isLoading}
          onClosed={(event) => {
            event.stopPropagation();
            setJobDrawerOpen(false);
          }}
        />
      )}
    </Drawer>
  );
};
